// AdminTeamOverviewPage.jsx — Admin-only Team Members Overview
// Location: src/pages/team/AdminTeamOverviewPage.jsx
//
// Dense review table with:
//   - All columns visible in compressed mode
//   - Per-cell expansion drawers for detailed content
//   - Sort/filter toolbar (mirrors ProjectTasksPage patterns)
//   - Editable fields save back to DB; read-only fields are visually distinct

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ArrowUpDown, Filter, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, X, Search, Lock, Unlock } from 'lucide-react';
import { useAdminTeamOverview } from '../../hooks/useAdminTeamOverview';
import { supabase } from '../../supabaseClient';
import { BrioDatePicker } from '../../components/BrioComponents';
import { getPersonalityColor, getPersonalityHex, getContrastColor, parseMbtiType, getPersonalityName, normalizeExpertiseArray } from '../../utils/personalityHelpers';
import TeamMemberFullScreenPage from './TeamMemberFullScreenPage';

// ══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════════════════

const ROLE_COLORS = {
  'Director': 'var(--brio-role-director)',
  'Partner': 'var(--brio-role-partner)',
  'Associate': 'var(--brio-role-associate)',
  'Senior Architect': 'var(--brio-role-senior-architect)',
  'Project Architect': 'var(--brio-role-project-architect)',
  'Medior Architect': 'var(--brio-role-medior-architect)',
  'Junior Architect': 'var(--brio-role-junior-architect)',
  'Intern': 'var(--brio-role-intern)',
  'Support': 'var(--brio-role-support)',
  'Model Maker': 'var(--brio-role-model-maker)',
  'Visualiser': 'var(--brio-role-visualiser)',
  'Others': 'var(--brio-role-others)',
};

const BRIO_GOLD = '#9f902a';
const BRIO_GREEN = '#689581';
const BRIO_RED = '#934948';
const BRIO_ORANGE = '#D4824A';
const BRIO_GREY = '#666666';
const BRIO_BORDER = '#e5e5e5';
const BRIO_BG = '#fafaf8';
const FONT = 'Raleway, sans-serif';

// ══════════════════════════════════════════════════════════════════════════
// COLUMN DEFINITIONS
// ══════════════════════════════════════════════════════════════════════════

const DEFAULT_COLUMNS = [
  { key: 'name',               label: 'Name',              sortable: true,  filterable: true,  minW: 120, flexW: 140, alwaysFrozen: true },
  { key: 'roleLevel',          label: 'Role / Level',      sortable: true,  filterable: true,  minW: 115, flexW: 135 },
  { key: 'personalityTest',    label: 'Personality',       sortable: true,  filterable: false, minW: 105, flexW: 120 },
  { key: 'salaryHistory',      label: 'Salary',            sortable: true,  filterable: false, minW: 90,  flexW: 120 },
  { key: 'bonus',              label: 'Bonus',             sortable: true,  filterable: false, minW: 90,  flexW: 120 },
  { key: 'joiningDate',        label: 'Joined',            sortable: true,  filterable: false, minW: 90,  flexW: 100 },
  { key: 'experience',         label: 'Exp (yrs)',         sortable: true,  filterable: false, minW: 90,  flexW: 100 },
  { key: 'graduationDate',     label: 'Grad Date',         sortable: true,  filterable: false, minW: 100, flexW: 110 },
  { key: 'prevEmployer',       label: 'Prev Employer',     sortable: true,  filterable: false, minW: 115, flexW: 135 },
  { key: 'expertise',          label: 'Expertise',         sortable: false, filterable: false, minW: 120, flexW: 180 },
  { key: 'planningMasters',    label: 'Plan M?',           sortable: true,  filterable: false, minW: 85,  flexW: 100 },
  { key: 'reviews',            label: 'Reviews',           sortable: false, filterable: false, minW: 100, flexW: 130 },
  { key: 'promotionReadiness', label: 'Promo',             sortable: true,  filterable: true,  minW: 80,  flexW: 90 },
  { key: 'perfRating',         label: 'Rating',            sortable: true,  filterable: false, minW: 85,  flexW: 100 },
  { key: 'degree',             label: 'Degree',            sortable: true,  filterable: true,  minW: 85,  flexW: 100 },
  { key: 'internship',         label: 'INTS',              sortable: true,  filterable: true,  minW: 70,  flexW: 80 },
  { key: 'designation',        label: 'Dsgtn',             sortable: true,  filterable: true,  minW: 80,  flexW: 90 },
  { key: 'noticePeriod',       label: 'Notice',            sortable: true,  filterable: false, minW: 80,  flexW: 90 },
  { key: 'homeCity',           label: 'Home Town',         sortable: true,  filterable: true,  minW: 105, flexW: 120 },
  { key: 'phone',              label: 'Phone',             sortable: false, filterable: false, minW: 110, flexW: 130 },
  { key: 'emailAddr',          label: 'Email',             sortable: false, filterable: false, minW: 120, flexW: 150 },
  { key: 'office',             label: 'Office',            sortable: true,  filterable: true,  minW: 75,  flexW: 85 },
  { key: 'dob',                label: 'DOB',               sortable: true,  filterable: false, minW: 90,  flexW: 100 },
];

// Role hierarchy sort order (lowest → highest), office-agnostic.
const ROLE_SORT_ORDER = [
  'support',              // 0
  'others',               // 1
  'visualiser',           // 2
  'model maker',          // 3
  'intern',               // 4
  'junior architect',     // 5
  'medior architect',     // 6
  'project architect',    // 7
  'senior architect',     // 8
  'associate',            // 9
  'associate architect',  // 10
  'partner',              // 11
  'principal',            // 12
  'director',             // 13
];

function getRoleSortValue(roleName, jobLevel) {
  // Normalize: strip any parenthesised suffix like "(9)" or "(NL)", trim, lowercase
  const name = (roleName || '').trim().replace(/\s*\([^)]*\)$/, '').trim().replace(/\s+/g, ' ').toLowerCase();
  if (!name) return 99990 + (Number(jobLevel) || 0); // empty → sort last
  // Exact match first
  let idx = ROLE_SORT_ORDER.indexOf(name);
  // Fuzzy: pick the longest known role substring contained in the input
  if (idx < 0) {
    let bestLen = 0;
    for (let i = 0; i < ROLE_SORT_ORDER.length; i++) {
      if (name.includes(ROLE_SORT_ORDER[i]) && ROLE_SORT_ORDER[i].length > bestLen) {
        idx = i;
        bestLen = ROLE_SORT_ORDER[i].length;
      }
    }
  }
  const base = idx >= 0 ? idx * 100 : 99990;
  return base + (Number(jobLevel) || 0);
}

// localStorage keys
const COL_ORDER_STORAGE_KEY = 'ato-col-order';
const FROZEN_COLS_STORAGE_KEY = 'ato-frozen-cols';

function loadStoredColumnOrder() {
  try {
    const saved = localStorage.getItem(COL_ORDER_STORAGE_KEY);
    if (!saved) return null;
    const order = JSON.parse(saved);
    // Validate: must be an array of keys that match DEFAULT_COLUMNS
    const defaultKeys = new Set(DEFAULT_COLUMNS.map(c => c.key));
    if (!Array.isArray(order) || order.length !== DEFAULT_COLUMNS.length) return null;
    if (!order.every(k => defaultKeys.has(k))) return null;
    return order;
  } catch { return null; }
}

function getOrderedColumns(order, frozenCols) {
  let cols;
  if (!order) {
    cols = [...DEFAULT_COLUMNS];
  } else {
    const map = {};
    DEFAULT_COLUMNS.forEach(c => { map[c.key] = c; });
    cols = order.map(k => map[k]).filter(Boolean);
  }
  // Auto-move frozen columns to the left (after alwaysFrozen ones)
  const alwaysFrozen = cols.filter(c => c.alwaysFrozen);
  const userFrozen = cols.filter(c => !c.alwaysFrozen && frozenCols[c.key]);
  const rest = cols.filter(c => !c.alwaysFrozen && !frozenCols[c.key]);
  return [...alwaysFrozen, ...userFrozen, ...rest];
}

const DEFAULT_FROZEN_COLS = { roleLevel: true, personalityTest: true };

function loadFrozenCols() {
  try {
    const saved = localStorage.getItem(FROZEN_COLS_STORAGE_KEY);
    return saved ? JSON.parse(saved) : DEFAULT_FROZEN_COLS;
  } catch { return DEFAULT_FROZEN_COLS; }
}

const FILTERABLE_COLUMNS = DEFAULT_COLUMNS.filter(c => c.filterable);

// ══════════════════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════════════════

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

function fmtShortDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

function fmtMonthYear(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  return dt.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

function fmtDesignation(v) {
  if (!v) return '—';
  const lower = v.toLowerCase();
  if (lower === 'consultant') return 'Con';
  if (lower === 'employee') return 'Emp';
  return v;
}

function fmtSalary(n) {
  if (n == null) return '—';
  const num = Number(n);
  if (isNaN(num)) return '—';
  if (num >= 100000) return (num / 100000).toFixed(1) + 'L';
  if (num >= 1000) return (num / 1000).toFixed(0) + 'k';
  return String(num);
}

// ══════════════════════════════════════════════════════════════════════════
// TOOLTIP COMPONENT
// ══════════════════════════════════════════════════════════════════════════

function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  if (!text) return children;

  // Viewport-aware: always expand right; below if near top, above if near bottom
  const getTooltipStyle = () => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const style = {
      position: 'fixed', padding: '6px 10px', borderRadius: '4px',
      background: '#333', color: '#fff', fontSize: '11px', fontWeight: 400,
      whiteSpace: 'pre-wrap', pointerEvents: 'none', zIndex: 10001,
      lineHeight: '15px', fontFamily: FONT, maxWidth: '300px', wordBreak: 'break-word',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    };
    // Horizontal: expand to the right, clamp to viewport
    const leftPos = Math.min(pos.x + 8, vw - 310);
    style.left = Math.max(4, leftPos) + 'px';
    // Vertical: if in bottom half, show above cursor; otherwise below
    if (pos.y > vh * 0.6) {
      style.bottom = (vh - pos.y + 8) + 'px';
    } else {
      style.top = (pos.y + 16) + 'px';
    }
    return style;
  };

  return (
    <span
      style={{ cursor: 'default' }}
      onMouseEnter={e => { setPos({ x: e.clientX, y: e.clientY }); setShow(true); }}
      onMouseMove={e => { setPos({ x: e.clientX, y: e.clientY }); }}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <span style={getTooltipStyle()}>
          {text}
        </span>
      )}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// TOOLBAR ICON (mirrors ProjectTasksPage)
// ══════════════════════════════════════════════════════════════════════════

function ToolbarIcon({ onClick, tooltip, active, badge, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        padding: '4px 6px',
        border: '1px solid ' + (active ? BRIO_GOLD : (hovered ? BRIO_GOLD : BRIO_BORDER)),
        borderRadius: '4px', background: 'transparent',
        color: active ? BRIO_GOLD : (hovered ? BRIO_GOLD : '#888'),
        cursor: 'pointer', transition: 'all 0.15s ease', position: 'relative',
        fontFamily: FONT, fontSize: '10px', fontWeight: 600, lineHeight: 1
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
      {badge != null && <span style={{ fontSize: '9px', fontWeight: 500 }}>({badge})</span>}
      {hovered && tooltip && (
        <span style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          marginBottom: '6px', padding: '3px 8px', borderRadius: '3px',
          background: '#333', color: '#fff', fontSize: '10px', fontWeight: 400,
          whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 300, lineHeight: '14px'
        }}>
          {tooltip}
        </span>
      )}
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SORT PANEL (mirrors ProjectTasksPage)
// ══════════════════════════════════════════════════════════════════════════

function SortPanel({ sortRules, onChangeSortRules, onClose }) {
  const panelRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const sortableCols = DEFAULT_COLUMNS.filter(c => c.sortable);
  const usedKeys = sortRules.map(r => r.key);
  const availableCols = sortableCols.filter(c => !usedKeys.includes(c.key));

  return (
    <div ref={panelRef} style={{
      position: 'absolute', top: '100%', left: 0, zIndex: 200,
      background: '#fff', border: '1px solid ' + BRIO_BORDER,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      minWidth: '320px', marginTop: '4px', padding: '12px',
      fontFamily: FONT,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sort</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: '2px' }}><X size={14} /></button>
      </div>
      {sortRules.map((rule, idx) => {
        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <span style={{ fontSize: '11px', color: BRIO_GREY, minWidth: '28px' }}>{idx === 0 ? 'by' : 'then'}</span>
            <select
              value={rule.key}
              onChange={e => {
                const next = [...sortRules];
                next[idx] = { ...next[idx], key: e.target.value };
                onChangeSortRules(next);
              }}
              style={{ flex: 1, fontSize: '11px', padding: '4px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', fontFamily: FONT }}
            >
              {sortableCols.filter(c => c.key === rule.key || !usedKeys.includes(c.key)).map(c => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <button
              onClick={() => {
                const next = [...sortRules];
                next[idx] = { ...next[idx], dir: rule.dir === 'asc' ? 'desc' : 'asc' };
                onChangeSortRules(next);
              }}
              style={{ background: 'none', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', cursor: 'pointer', padding: '3px 6px', fontSize: '10px', color: BRIO_GOLD, fontFamily: FONT }}
            >
              {rule.dir === 'asc' ? '↑ Asc' : '↓ Desc'}
            </button>
            <button
              onClick={() => onChangeSortRules(sortRules.filter((_, i) => i !== idx))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO_RED, padding: '2px' }}
            ><X size={12} /></button>
          </div>
        );
      })}
      {availableCols.length > 0 && (
        <button
          onClick={() => onChangeSortRules([...sortRules, { key: availableCols[0].key, dir: 'asc' }])}
          style={{ fontSize: '11px', color: BRIO_GOLD, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, fontWeight: 500, padding: '4px 0' }}
        >
          + Add sort rule
        </button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// FILTER PANEL (mirrors ProjectTasksPage)
// ══════════════════════════════════════════════════════════════════════════

function FilterPanel({ filterRules, onChangeFilterRules, onClose, uniqueValues }) {
  const panelRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div ref={panelRef} style={{
      position: 'absolute', top: '100%', left: 0, zIndex: 200,
      background: '#fff', border: '1px solid ' + BRIO_BORDER,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      minWidth: '380px', marginTop: '4px', padding: '12px',
      fontFamily: FONT,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Filter</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: '2px' }}><X size={14} /></button>
      </div>
      {filterRules.map((rule, idx) => {
        const vals = uniqueValues[rule.key] || [];
        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <select
              value={rule.key}
              onChange={e => {
                const next = [...filterRules];
                next[idx] = { key: e.target.value, condition: 'is', value: '' };
                onChangeFilterRules(next);
              }}
              style={{ fontSize: '11px', padding: '4px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', fontFamily: FONT, minWidth: '90px' }}
            >
              {FILTERABLE_COLUMNS.map(c => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <select
              value={rule.condition}
              onChange={e => {
                const next = [...filterRules];
                next[idx] = { ...next[idx], condition: e.target.value };
                onChangeFilterRules(next);
              }}
              style={{ fontSize: '11px', padding: '4px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', fontFamily: FONT }}
            >
              <option value="is">is</option>
              <option value="is not">is not</option>
              <option value="contains">contains</option>
            </select>
            {rule.condition === 'contains' ? (
              <input
                type="text"
                value={rule.value}
                onChange={e => {
                  const next = [...filterRules];
                  next[idx] = { ...next[idx], value: e.target.value };
                  onChangeFilterRules(next);
                }}
                style={{ flex: 1, fontSize: '11px', padding: '4px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', fontFamily: FONT }}
                placeholder="Search..."
              />
            ) : (
              <select
                value={rule.value}
                onChange={e => {
                  const next = [...filterRules];
                  next[idx] = { ...next[idx], value: e.target.value };
                  onChangeFilterRules(next);
                }}
                style={{ flex: 1, fontSize: '11px', padding: '4px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', fontFamily: FONT }}
              >
                <option value="">— Select —</option>
                {vals.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            )}
            <button
              onClick={() => onChangeFilterRules(filterRules.filter((_, i) => i !== idx))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO_RED, padding: '2px' }}
            ><X size={12} /></button>
          </div>
        );
      })}
      <button
        onClick={() => onChangeFilterRules([...filterRules, { key: FILTERABLE_COLUMNS[0].key, condition: 'is', value: '' }])}
        style={{ fontSize: '11px', color: BRIO_GOLD, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, fontWeight: 500, padding: '4px 0' }}
      >
        + Add filter rule
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// EDITABLE CELL — inline text/date/select editing for editable fields
// ══════════════════════════════════════════════════════════════════════════

function EditableCell({ value, onChange, type, options, placeholder, style: extraStyle, displayValue }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== (value || '')) onChange(draft);
  };

  // Date fields use BrioDatePicker directly (no edit/display toggle needed)
  if (type === 'date') {
    return (
      <BrioDatePicker
        value={value || ''}
        onChange={v => onChange(v)}
        clearable
        className="brio-datepicker-trigger--compact"
        style={{ width: '100%', maxWidth: '95px', fontSize: '10px', boxSizing: 'border-box', ...extraStyle }}
      />
    );
  }

  if (!editing) {
    return (
      <span
        onClick={() => setEditing(true)}
        style={{
          cursor: 'pointer', display: 'block', minHeight: '16px',
          borderBottom: '1px dashed ' + BRIO_GOLD + '60',
          color: value ? '#1e293b' : BRIO_GREY,
          fontSize: '11px', lineHeight: '16px',
          ...extraStyle,
        }}
        title="Click to edit"
      >
        {displayValue || value || placeholder || '—'}
      </span>
    );
  }

  if (type === 'select') {
    return (
      <select
        ref={inputRef}
        value={draft}
        onChange={e => { setDraft(e.target.value); }}
        onBlur={commit}
        style={{ fontSize: '11px', padding: '1px 4px', border: '1px solid ' + BRIO_GOLD, borderRadius: '2px', fontFamily: FONT, width: '100%' }}
      >
        <option value="">—</option>
        {(options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit(); }}
      style={{ fontSize: '11px', padding: '1px 4px', border: '1px solid ' + BRIO_GOLD, borderRadius: '2px', fontFamily: FONT, width: '100%' }}
    />
  );
}

// ══════════════════════════════════════════════════════════════════════════
// EXPANSION DRAWER — per-cell detail slide-out
// ══════════════════════════════════════════════════════════════════════════

function ExpansionDrawer({ title, onClose, children }) {
  const drawerRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div ref={drawerRef} style={{
      position: 'fixed', top: 0, right: 0, bottom: 0,
      width: '360px', maxWidth: '90vw',
      background: '#fff', borderLeft: '2px solid ' + BRIO_GOLD,
      boxShadow: '-8px 0 24px rgba(0,0,0,0.12)',
      zIndex: 500, overflowY: 'auto',
      fontFamily: FONT, padding: '20px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>{title}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}><X size={16} /></button>
      </div>
      {children}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// REVIEW LINK — small pill linking to a review
// ══════════════════════════════════════════════════════════════════════════

function ReviewPill({ review, onClick }) {
  const summary = review.sectionF?.overallSummary || '';
  const shortSummary = summary.length > 30 ? summary.slice(0, 30) + '…' : (summary || '—');
  return (
    <Tooltip text={summary || 'No summary'}>
      <span
        onClick={onClick}
        style={{
          display: 'inline-block', fontSize: '10px', padding: '2px 6px',
          background: BRIO_BG, border: '1px solid ' + BRIO_BORDER, borderRadius: '3px',
          cursor: 'pointer', color: BRIO_GOLD, fontWeight: 500,
          maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          marginBottom: '2px',
        }}
      >
        {fmtShortDate(review.reviewDate)} — {shortSummary}
      </span>
    </Tooltip>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// RATING PILL — editable inline rating for eval reports
// ══════════════════════════════════════════════════════════════════════════

function RatingPill({ value, primary, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value != null ? String(value) : '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value != null ? String(value) : ''); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    const newVal = draft.trim();
    const oldVal = value != null ? String(value) : '';
    if (newVal !== oldVal) onSave(newVal);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        step="0.1"
        min="0"
        max="5"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        style={{
          width: '36px', fontSize: '10px', padding: '1px 3px',
          border: '1px solid ' + BRIO_GOLD, borderRadius: '2px',
          fontFamily: FONT, textAlign: 'center',
        }}
      />
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      style={{
        display: 'inline-block', fontSize: '10px', padding: '1px 4px',
        background: primary ? BRIO_GOLD + '15' : '#f0f0ed',
        border: '1px solid ' + (primary ? BRIO_GOLD + '30' : BRIO_BORDER),
        borderRadius: '2px', marginRight: '2px',
        fontWeight: primary ? 600 : 400,
        color: primary ? BRIO_GOLD : BRIO_GREY,
        cursor: 'pointer',
        borderBottom: '1px dashed ' + BRIO_GOLD + '60',
      }}
      title="Click to edit"
    >
      {value != null ? '\u2605' + value : '—'}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// BONUS CELL — single editable cell showing Q1-Q4 with inline editor
// ══════════════════════════════════════════════════════════════════════════

function BonusCell({ bonus, total, officeLocation, onSave }) {
  const [editing, setEditing] = useState(false);
  const [q1, setQ1] = useState('');
  const [q2, setQ2] = useState('');
  const [q3, setQ3] = useState('');
  const [q4, setQ4] = useState('');
  const panelRef = useRef(null);

  useEffect(() => {
    if (editing) {
      setQ1(bonus?.q1 || '');
      setQ2(bonus?.q2 || '');
      setQ3(bonus?.q3 || '');
      setQ4(bonus?.q4 || '');
    }
  }, [editing, bonus]);

  useEffect(() => {
    if (!editing) return;
    function handleClickOutside(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setEditing(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editing]);

  const commit = () => {
    setEditing(false);
    onSave(parseFloat(q1) || 0, parseFloat(q2) || 0, parseFloat(q3) || 0, parseFloat(q4) || 0);
  };

  const currency = officeLocation === 'NL' ? 'EUR' : 'INR';
  const tipText = total > 0
    ? `${currency} ${total.toLocaleString()} (Q1:${fmtSalary(bonus?.q1)} Q2:${fmtSalary(bonus?.q2)} Q3:${fmtSalary(bonus?.q3)} Q4:${fmtSalary(bonus?.q4)})`
    : null;

  if (editing) {
    const inputStyle = {
      width: '52px', fontSize: '10px', padding: '2px 3px',
      border: '1px solid ' + BRIO_GOLD, borderRadius: '2px',
      fontFamily: FONT, textAlign: 'right',
    };
    return (
      <div ref={panelRef} style={{
        position: 'absolute', zIndex: 100, background: '#fff',
        border: '1px solid ' + BRIO_GOLD, borderRadius: '4px',
        padding: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 6px', alignItems: 'center',
      }}>
        <span style={{ fontSize: '9px', color: BRIO_GREY }}>Q1</span>
        <input type="number" value={q1} onChange={e => setQ1(e.target.value)} style={inputStyle} />
        <span style={{ fontSize: '9px', color: BRIO_GREY }}>Q2</span>
        <input type="number" value={q2} onChange={e => setQ2(e.target.value)} style={inputStyle} />
        <span style={{ fontSize: '9px', color: BRIO_GREY }}>Q3</span>
        <input type="number" value={q3} onChange={e => setQ3(e.target.value)} style={inputStyle} />
        <span style={{ fontSize: '9px', color: BRIO_GREY }}>Q4</span>
        <input type="number" value={q4} onChange={e => setQ4(e.target.value)}
          style={inputStyle}
          onKeyDown={e => { if (e.key === 'Enter') commit(); }}
        />
        <button onClick={commit} style={{
          gridColumn: '1 / -1', fontSize: '10px', padding: '3px 8px',
          background: BRIO_GOLD, color: '#fff', border: 'none',
          borderRadius: '3px', cursor: 'pointer', fontFamily: FONT, fontWeight: 600,
          marginTop: '2px',
        }}>Save</button>
      </div>
    );
  }

  return (
    <Tooltip text={tipText}>
      <span
        onClick={() => setEditing(true)}
        style={{
          cursor: 'pointer', display: 'block', fontSize: '10px',
          borderBottom: '1px dashed ' + BRIO_GOLD + '60',
          color: total > 0 ? '#333' : BRIO_GREY,
        }}
        title="Click to edit"
      >
        {total > 0 ? fmtSalary(total) : '—'}
      </span>
    </Tooltip>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// FROZEN COLUMN LEFT OFFSET CALCULATOR
// ══════════════════════════════════════════════════════════════════════════

function computeFrozenLeft(columns, collapsedCols, frozenCols, colIdx, collapsedW) {
  // Sum widths of all frozen columns before this one
  let left = 0;
  for (let i = 0; i < colIdx; i++) {
    const c = columns[i];
    const isFr = c.alwaysFrozen || frozenCols[c.key];
    if (!isFr) continue;
    left += collapsedCols[c.key] ? collapsedW : c.flexW;
  }
  return left;
}

// ══════════════════════════════════════════════════════════════════════════
// CELL RENDERER — renders a single cell by column key
// ══════════════════════════════════════════════════════════════════════════

function renderCell(key, row, colIdx, ctx) {
  const { ROW_STYLE, RO_STYLE, openFullscreen, handleFieldSave, setDrawer, handlePromotionSave, handleRatingSave, handleBonusSave } = ctx;

  switch (key) {
    case 'name':
      return (
        <td key={key} style={{ ...ROW_STYLE, fontWeight: 600, color: BRIO_GOLD }}>
          <Tooltip text={row.profileIntro || null}>
            <span style={{ cursor: 'pointer' }} onClick={() => openFullscreen(row.userId)}>
              {row.name}
            </span>
          </Tooltip>
        </td>
      );
    case 'phone':
      return (
        <td key={key} style={ROW_STYLE}>
          <EditableCell
            value={row.phone}
            onChange={v => handleFieldSave(row.userId, 'phoneMobile', v)}
            placeholder="—"
          />
        </td>
      );
    case 'emailAddr':
      return (
        <td key={key} style={ROW_STYLE}>
          <Tooltip text={row.emailPersonal ? `Personal: ${row.emailPersonal}` : null}>
            <EditableCell
              value={row.emailAddr}
              onChange={v => handleFieldSave(row.userId, 'emailOffice', v)}
              placeholder="—"
            />
          </Tooltip>
        </td>
      );
    case 'office':
      return (
        <td key={key} style={ROW_STYLE}>
          <EditableCell
            value={row.office}
            type="select"
            options={['NL', 'IN']}
            onChange={v => handleFieldSave(row.userId, 'officeLocation', v)}
            displayValue={
              <span style={{
                display: 'inline-block', fontSize: '10px', padding: '1px 5px', borderRadius: '3px',
                background: row.office === 'NL' ? '#e8f0fe' : '#fef3e0',
                color: row.office === 'NL' ? '#1a56db' : '#b45309',
                fontWeight: 500,
              }}>
                {row.office || '—'}
              </span>
            }
          />
        </td>
      );
    case 'dob':
      return (
        <td key={key} style={{ ...ROW_STYLE, overflow: 'visible', position: 'relative' }}>
          <EditableCell value={row.dob} type="date" onChange={v => handleFieldSave(row.userId, 'dob', v)} />
        </td>
      );
    case 'homeCity':
      return (
        <td key={key} style={ROW_STYLE}>
          <EditableCell value={row.homeCity} onChange={v => handleFieldSave(row.userId, 'homeCity', v)} placeholder="—" />
        </td>
      );
    case 'roleLevel':
      return (
        <td key={key} style={{ ...ROW_STYLE, ...RO_STYLE }}>
          {row.roleLevelRaw ? (
            <Tooltip text={row.roleLevel}>
              <span style={{
                background: ROLE_COLORS[row.roleLevelRaw] || '#D4C4A0',
                color: '#fff',
                display: 'inline-block',
                minWidth: 135,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                padding: '2px 8px',
                fontSize: 'var(--brio-font-size-s, 11px)',
                fontFamily: 'Raleway, sans-serif',
                fontWeight: 500,
                borderRadius: 2,
              }}>
                {row.roleLevel}
              </span>
            </Tooltip>
          ) : (
            <span style={{ color: '#aaa' }}>—</span>
          )}
        </td>
      );
    case 'designation':
      return (
        <td key={key} style={ROW_STYLE}>
          <EditableCell value={row.designation} type="select" options={['employee', 'consultant']}
            onChange={v => handleFieldSave(row.userId, 'designation', v)} displayValue={fmtDesignation(row.designation)} />
        </td>
      );
    case 'personalityTest': {
      const pType = parseMbtiType(row.personalityTest);
      const pHex = pType ? getPersonalityHex(row.personalityTest) : null;
      const pContrast = pHex ? getContrastColor(pHex) : null;
      const pName = pType ? getPersonalityName(row.personalityTest) : null;
      // Extract full MBTI code with assertion letter (e.g. "ENTJ-A") from raw value
      const pFullCode = pType && row.personalityTest
        ? (row.personalityTest.match(/[IE][NnSs][TtFf][JjPp][-]?[AaTt]?/i) || [pType])[0].toUpperCase()
        : pType;
      const personalityDisplay = pType ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            display: 'inline-block', padding: '1px 5px', borderRadius: '2px',
            backgroundColor: pHex, color: pContrast, flexShrink: 0,
            fontSize: '9px', fontWeight: 600, lineHeight: '14px',
          }}>{pFullCode}</span>
          {pName && <span style={{ fontSize: '11px', color: '#1e293b' }}>{pName}</span>}
        </span>
      ) : null;
      return (
        <td key={key} style={ROW_STYLE}>
          <EditableCell
            value={row.personalityTest}
            onChange={v => handleFieldSave(row.userId, 'personalityTestResult', v)}
            placeholder="—"
            displayValue={personalityDisplay}
          />
        </td>
      );
    }
    case 'expertise': {
      const tags = normalizeExpertiseArray(row.expertise);
      const MAX_SHOW = 3;
      return (
        <td key={key} style={ROW_STYLE}>
          {tags.length === 0 ? (
            <span style={{ color: '#9ca3af', fontSize: '11px' }}>—</span>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
              {tags.slice(0, MAX_SHOW).map(t => (
                <span key={t} style={{
                  padding: '1px 5px', background: 'rgba(159,144,42,0.1)',
                  color: BRIO_GOLD, fontSize: '9px', fontWeight: 500,
                  borderRadius: '2px', lineHeight: '16px', whiteSpace: 'nowrap',
                }}>{t}</span>
              ))}
              {tags.length > MAX_SHOW && (
                <span style={{ fontSize: '9px', color: BRIO_GOLD, fontWeight: 500, lineHeight: '16px' }}>
                  +{tags.length - MAX_SHOW}
                </span>
              )}
            </div>
          )}
        </td>
      );
    }
    case 'joiningDate':
      return (
        <td key={key} style={{ ...ROW_STYLE, overflow: 'visible', position: 'relative' }}>
          <EditableCell value={row.joiningDate} type="date" onChange={v => handleFieldSave(row.userId, 'joiningDate', v)} />
        </td>
      );
    case 'prevEmployer':
      return (
        <td key={key} style={{ ...ROW_STYLE, ...RO_STYLE }}>
          {row.emps.length > 0 ? (
            <span>
              <Tooltip text={row.prevEmployer ? `${row.prevEmployer.role} at ${row.prevEmployer.name}` : ''}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{row.prevEmployerName}</span>
              </Tooltip>
              {row.emps.length > 1 && (
                <span onClick={() => setDrawer({ userId: row.userId, column: 'employers', data: row })}
                  style={{ fontSize: '9px', color: BRIO_GOLD, cursor: 'pointer', fontWeight: 500 }}>
                  +{row.emps.length - 1} more
                </span>
              )}
            </span>
          ) : '—'}
        </td>
      );
    case 'experience':
      return (
        <td key={key} style={{ ...ROW_STYLE, ...RO_STYLE, textAlign: 'center' }}>
          {row.experience != null ? row.experience + 'y' : '—'}
        </td>
      );
    case 'salaryHistory':
      return (
        <td key={key} style={{ ...ROW_STYLE, ...RO_STYLE }}>
          {row.salaryEntries.length > 0 ? (
            <span style={{ display: 'inline-flex', gap: '2px', flexWrap: 'nowrap' }}>
              {row.salaryEntries.map((se, i) => {
                const periodLabel = fmtShortDate(se.from) + ' — ' + (se.to ? fmtShortDate(se.to) : 'Present');
                const currency = row.officeLocation === 'NL' ? 'EUR' : 'INR';
                const tipText = se.salary ? `${currency} ${Number(se.salary).toLocaleString()}/mo\n${periodLabel}` : periodLabel;
                return (
                  <Tooltip key={i} text={tipText}>
                    <span style={{
                      display: 'inline-block', fontSize: '10px', padding: '1px 4px',
                      background: i === 0 ? BRIO_GOLD + '15' : '#f0f0ed',
                      border: '1px solid ' + (i === 0 ? BRIO_GOLD + '30' : BRIO_BORDER),
                      borderRadius: '2px', fontWeight: i === 0 ? 600 : 400,
                      color: i === 0 ? BRIO_GOLD : BRIO_GREY,
                    }}>
                      {se.salary ? fmtSalary(se.salary) : '—'}
                    </span>
                  </Tooltip>
                );
              })}
            </span>
          ) : <span style={{ color: BRIO_GREY, fontSize: '10px' }}>—</span>}
        </td>
      );
    case 'bonus':
      return (
        <td key={key} style={{ ...ROW_STYLE, overflow: 'visible', position: 'relative' }}>
          <BonusCell
            bonus={row.bonus}
            total={row.bonusTotal}
            officeLocation={row.officeLocation}
            onSave={(q1, q2, q3, q4) => handleBonusSave(row.userId, q1, q2, q3, q4)}
          />
        </td>
      );
    case 'noticePeriod':
      return (
        <td key={key} style={{ ...ROW_STYLE, ...RO_STYLE, textAlign: 'center' }}>
          {row.noticePeriod != null ? row.noticePeriod + 'm' : '—'}
        </td>
      );
    case 'graduationDate':
      return (
        <td key={key} style={{ ...ROW_STYLE, overflow: 'visible', position: 'relative' }}>
          <EditableCell value={row.graduationDate} type="date" onChange={v => handleFieldSave(row.userId, 'graduationDate', v)} />
        </td>
      );
    case 'degree':
      return (
        <td key={key} style={{ ...ROW_STYLE, ...RO_STYLE }}>
          <Tooltip text={row.degreeTooltip}><span>{row.degreeLabel}</span></Tooltip>
        </td>
      );
    case 'internship':
      return (
        <td key={key} style={{ ...ROW_STYLE, ...RO_STYLE, textAlign: 'center' }}>
          <Tooltip text={row.internTooltip}>
            <span style={{
              display: 'inline-block', fontSize: '10px', padding: '1px 5px', borderRadius: '3px',
              background: row.hadInternship ? BRIO_GREEN + '15' : '#f0f0ed',
              color: row.hadInternship ? BRIO_GREEN : BRIO_GREY, fontWeight: 500,
            }}>
              {row.hadInternship ? 'Yes' : 'No'}
            </span>
          </Tooltip>
        </td>
      );
    case 'planningMasters':
      return (
        <td key={key} style={ROW_STYLE}>
          <EditableCell value={row.planningMasters} onChange={v => handleFieldSave(row.userId, 'planningMasters', v)} placeholder="—" />
        </td>
      );
    case 'reviews':
      return (
        <td key={key} style={{ ...ROW_STYLE, ...RO_STYLE }}>
          {row.last3Reviews.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
              {row.last3Reviews.map((review) => (
                <ReviewPill key={review.id} review={review}
                  onClick={() => setDrawer({ userId: row.userId, column: 'reviews', data: row })} />
              ))}
            </div>
          ) : <span style={{ color: BRIO_GREY, fontSize: '10px' }}>—</span>}
        </td>
      );
    case 'promotionReadiness':
      return (
        <td key={key} style={{ ...ROW_STYLE, textAlign: 'center' }}>
          {row.latestReviewId ? (
            <EditableCell
              value={row.promotionReadiness === '—' ? '' : row.promotionReadiness}
              type="select" options={['Ready', 'Possible', 'Not yet', 'N/A']}
              onChange={v => handlePromotionSave(row.latestReviewId, v)}
              displayValue={row.promotionReadiness}
              style={{
                fontSize: '10px', padding: '1px 5px', borderRadius: '3px', fontWeight: 500, textAlign: 'center',
                background: row.promotionReadiness === 'Ready' ? BRIO_GREEN + '15'
                  : row.promotionReadiness === 'Possible' ? BRIO_ORANGE + '15' : '#f0f0ed',
                color: row.promotionReadiness === 'Ready' ? BRIO_GREEN
                  : row.promotionReadiness === 'Possible' ? BRIO_ORANGE : BRIO_GREY,
              }}
            />
          ) : <span style={{ fontSize: '10px', color: BRIO_GREY }}>—</span>}
        </td>
      );
    case 'perfRating':
      return (
        <td key={key} style={ROW_STYLE}>
          {row.last3Evals.length > 0 ? (
            <span>
              {row.last3Evals.map((ev, i) => (
                <Tooltip key={ev.id} text={fmtMonthYear(ev.evaluationDate)}>
                  <RatingPill value={ev.performanceRating} primary={i === 0}
                    onSave={val => handleRatingSave(ev.id, val)} />
                </Tooltip>
              ))}
            </span>
          ) : <span style={{ color: BRIO_GREY, fontSize: '10px' }}>—</span>}
        </td>
      );
    default:
      return <td key={key} style={ROW_STYLE}>—</td>;
  }
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════

export default function AdminTeamOverviewPage({
  currentUser,
  activeUsers,
  activeTeams,
  supabaseUsers,
  getEmployeeNoticeEntitlement,
  selectedLeaveYear,
  getSalaryFromMatrix,
  getSalaryFromMatrixHistory,
  getSalaryFromNLMatrixHistory,
  activeTimeEntries,
  activeProjects,
  getInternalCostRate,
}) {
  const overview = useAdminTeamOverview();

  // ── Monthly salary ledger (same source as SalaryCostPage) ────────────
  // Fetched once: all salary_monthly_ledger rows up to and including the
  // current month (future rebuild-horizon rows are excluded). Map:
  // auth_user_id → [{month, base_salary}] sorted descending by month. Each
  // role is later valued at its reference month against this ledger, exactly
  // as the Salary Cost page resolves the salary for the month it displays.
  // (Previously this only fetched pre-2026 rows, which froze every ongoing
  //  role at its last 2025 salary and ignored any 2026+ raise.)
  const [ledgerMap, setLedgerMap] = useState({});
  useEffect(() => {
    const currentMonthStart = new Date().toISOString().slice(0, 7) + '-01';
    supabase
      .from('salary_monthly_ledger')
      .select('auth_user_id, month, base_salary')
      .lte('month', currentMonthStart)
      .order('month', { ascending: false })
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(row => {
          if (!map[row.auth_user_id]) map[row.auth_user_id] = [];
          map[row.auth_user_id].push({ month: row.month, baseSalary: row.base_salary });
        });
        setLedgerMap(map);
      });
  }, []);

  // ── Fullscreen member overlay ────────────────────────────────────────
  const [fullscreenUserId, setFullscreenUserId] = useState(null);
  const openFullscreen = useCallback((userId) => setFullscreenUserId(userId), []);
  const closeFullscreen = useCallback(() => setFullscreenUserId(null), []);

  // ── Sort / Filter state ──────────────────────────────────────────────
  const [sortRules, setSortRules] = useState([{ key: 'name', dir: 'asc' }]);
  const [filterRules, setFilterRules] = useState([]);
  const [showSortPanel, setShowSortPanel] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showAllUsers, setShowAllUsers] = useState(false);

  // ── Drawer state ─────────────────────────────────────────────────────
  const [drawer, setDrawer] = useState(null); // { userId, column, data }

  // ── Collapsible columns state ─────────────────────────────────────
  const COLLAPSED_W = 18; // width of a fully collapsed column (just the chevron)
  const COLLAPSED_STORAGE_KEY = 'ato-collapsed-cols';
  const [collapsedCols, setCollapsedCols] = useState(() => {
    try {
      const saved = localStorage.getItem(COLLAPSED_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const toggleColCollapse = useCallback((key) => {
    setCollapsedCols(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  // ── Column order (drag-to-reorder, persisted) ──────────────────────
  // ── Frozen columns (sticky, persisted) ─────────────────────────────
  const [frozenCols, setFrozenCols] = useState(() => loadFrozenCols());
  const toggleFreezeCol = useCallback((key) => {
    setFrozenCols(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(FROZEN_COLS_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  // ── Column order (drag-to-reorder, persisted) ──────────────────────
  const [columnOrder, setColumnOrder] = useState(() => loadStoredColumnOrder());
  const orderedColumns = useMemo(() => getOrderedColumns(columnOrder, frozenCols), [columnOrder, frozenCols]);

  const moveColumn = useCallback((fromIdx, toIdx) => {
    setColumnOrder(prev => {
      // Work against the current visual order (which accounts for frozen reordering)
      const currentKeys = getOrderedColumns(prev, frozenCols).map(c => c.key);
      // Don't move before the first frozen block
      const firstNonFrozen = currentKeys.findIndex(k => {
        const col = DEFAULT_COLUMNS.find(c => c.key === k);
        return !col?.alwaysFrozen && !frozenCols[k];
      });
      if (toIdx < firstNonFrozen && toIdx < fromIdx) return prev;
      const next = [...currentKeys];
      const [removed] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, removed);
      try { localStorage.setItem(COL_ORDER_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [frozenCols]);

  // ── Drag-to-reorder state ──────────────────────────────────────────
  const dragColIdxRef = useRef(null);
  const [dragOverColIdx, setDragOverColIdx] = useState(null);

  // ── Touch drag support (iPad) ─────────────────────────────────────
  const touchStartColRef = useRef(null);
  const touchCurrentColRef = useRef(null);
  const handleTouchStart = useCallback((colIdx, col, e) => {
    if (col.alwaysFrozen || frozenCols[col.key]) return;
    touchStartColRef.current = colIdx;
    touchCurrentColRef.current = colIdx;
    dragColIdxRef.current = colIdx;
  }, [frozenCols]);
  const handleTouchMove = useCallback((e) => {
    if (touchStartColRef.current == null) return;
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (el) {
      const th = el.closest('th');
      if (th && th.dataset.colIdx != null) {
        const idx = Number(th.dataset.colIdx);
        touchCurrentColRef.current = idx;
        setDragOverColIdx(idx);
      }
    }
  }, []);
  const handleTouchEnd = useCallback(() => {
    const from = touchStartColRef.current;
    const to = touchCurrentColRef.current;
    if (from != null && to != null && from !== to) {
      moveColumn(from, to);
    }
    touchStartColRef.current = null;
    touchCurrentColRef.current = null;
    dragColIdxRef.current = null;
    setDragOverColIdx(null);
  }, [moveColumn]);

  // ── Build row data ───────────────────────────────────────────────────
  const rows = useMemo(() => {
    if (overview.loading) return [];
    return activeUsers.map(user => {
      const profile = overview.getProfileForUser(user.id);
      const edu = overview.getEducationForUser(user.id);
      const emps = overview.getEmployersForUser(user.id);
      const reviews = overview.getReviewsForUser(user.id);
      const evals = overview.getEvalReportsForUser(user.id);
      const roleHist = overview.getRoleHistoryForUser(user.id);

      // Computed: total experience
      const bachelorEdu = edu.find(e => (e.type || '').toLowerCase() === 'bachelor');
      const bachelorGradDate = bachelorEdu?.graduationDate;
      let totalExperience = null;
      if (bachelorGradDate) {
        const start = new Date(bachelorGradDate);
        const now = new Date();
        const rawYears = (now - start) / (365.25 * 24 * 60 * 60 * 1000);
        const gap = Number(profile?.experienceGapYears) || 0;
        totalExperience = Math.max(0, rawYears - gap).toFixed(1);
      }

      // Computed: highest degree
      const masterEdu = edu.find(e => (e.type || '').toLowerCase() === 'master');
      const highestDegree = masterEdu || bachelorEdu || null;
      const degreeLabel = masterEdu ? 'Masters' : (bachelorEdu ? 'Bachelors' : '—');
      const degreeTooltip = highestDegree ? highestDegree.institution : '';

      // Computed: internship (employer before graduation)
      const gradDate = bachelorGradDate ? new Date(bachelorGradDate) : null;
      const internEmployer = gradDate
        ? emps.find(e => e.endDate && new Date(e.endDate) <= gradDate)
        : null;
      const hadInternship = !!internEmployer;
      const internTooltip = internEmployer ? internEmployer.name : '';

      // Notice period (computed)
      let noticePeriod = null;
      if (getEmployeeNoticeEntitlement) {
        const year = selectedLeaveYear || new Date().getFullYear();
        const ent = getEmployeeNoticeEntitlement(user.id, year);
        if (ent) noticePeriod = ent.finalTotalNotice;
      }

      // Last 3 reviews
      const last3Reviews = reviews.slice(0, 3);

      // Promotion readiness (from latest review's sectionF)
      const latestReview = reviews[0];
      const promotionReadiness = latestReview?.sectionF?.promotionReadiness || '—';
      const latestReviewId = latestReview?.id || null;

      // Last 3 eval ratings
      const last3Evals = evals.slice(0, 3);

      // Office location
      const officeLocation = profile?.officeLocation || user.officeLocation || 'NL';

      // Contact info
      const phoneMobile = profile?.phoneMobile || '';
      const emailAddr = profile?.emailOffice || user.email || '';
      const emailPersonal = profile?.emailPersonal || '';

      // Bonus Q1-Q4 for current year
      const currentYear = new Date().getFullYear();
      const yearBonuses = user.bonuses?.[currentYear] || { q1: 0, q2: 0, q3: 0, q4: 0 };

      // Last 3 salaries from role history — each valued at its reference month:
      // the role's end month for past roles, or the current month for the active
      // (open-ended) role. The monthly ledger (salary_monthly_ledger) is the
      // source of truth, exactly as on the Salary Cost page: we take the most
      // recent ledger salary in effect during the role, at or before its
      // reference month. The salary matrix is only a fallback when the ledger has
      // no salary for that role period (e.g. a brand-new hire whose derived
      // ledger month is not yet populated). The lookup is bounded to the role's
      // own period so a prior role's salary never leaks into a later role.
      const today = new Date().toISOString().slice(0, 10);
      const currentMonthStart = today.slice(0, 7) + '-01';
      const userLedgerRows = ledgerMap[user.authUserId] || []; // sorted desc by month
      const last3Roles = roleHist.slice(0, 3);
      const salaryEntries = last3Roles.map(rh => {
        const refMonth = rh.effectiveTo
          ? rh.effectiveTo.slice(0, 7) + '-01'   // first of the role's end month
          : currentMonthStart;                   // active role → current month
        const roleStartMonth = rh.effectiveFrom
          ? rh.effectiveFrom.slice(0, 7) + '-01'
          : '0000-01-01';
        let salary = null;

        // 1. Ledger salary in effect during this role: the most recent non-zero
        //    row within [roleStart, refMonth]. This is the same source and the
        //    same month the Salary Cost page resolves for its display.
        const ledgerRow = userLedgerRows.find(
          r => r.month <= refMonth && r.month >= roleStartMonth && Number(r.baseSalary) > 0
        );
        if (ledgerRow) {
          salary = Number(ledgerRow.baseSalary);
        } else {
          // 2. No ledger salary for this role period — fall back to the salary
          //    matrix (valued at the reference month) so the pill still shows a
          //    value.
          if (officeLocation === 'NL') {
            if (getSalaryFromNLMatrixHistory && rh.jobRole && rh.jobLevel != null) {
              const s = getSalaryFromNLMatrixHistory(rh.jobRole, rh.jobLevel, refMonth);
              if (s > 0) salary = s;
            }
          } else {
            if (getSalaryFromMatrixHistory && rh.jobRole && rh.jobLevel != null) {
              const s = getSalaryFromMatrixHistory(rh.jobRole, rh.jobLevel, refMonth);
              if (s > 0) salary = s;
            }
          }
          if (salary == null && getSalaryFromMatrix && rh.jobRole && rh.jobLevel != null) {
            const result = getSalaryFromMatrix(rh.jobRole, rh.jobLevel);
            if (result && result.salary > 0) salary = result.salary;
          }
        }

        return { salary, from: rh.effectiveFrom, to: rh.effectiveTo, role: rh.jobRole, level: rh.jobLevel };
      });
      const matrixSalary = salaryEntries[0]?.salary || null;

      // Most recent employer
      const prevEmployer = emps[0] || null;

      return {
        userId: user.id,
        userName: user.name || '—',
        userStatus: user.status,
        isActive: user.isActive,
        profile,
        edu,
        emps,
        // Display fields
        name: user.name || '—',
        phone: phoneMobile,
        emailAddr,
        emailPersonal,
        office: officeLocation,
        dob: profile?.dob || null,
        homeCity: profile?.homeCity || '',
        roleLevel: (profile?.currentJobRole || roleHist[0]?.jobRole || user.jobRole || '') + (profile?.currentJobLevel != null ? ' L' + profile.currentJobLevel : (roleHist[0]?.jobLevel != null ? ' L' + roleHist[0].jobLevel : (user.jobLevel != null ? ' L' + user.jobLevel : ''))),
        roleLevelRaw: profile?.currentJobRole || roleHist[0]?.jobRole || user.jobRole || '',
        roleSortOrder: getRoleSortValue(
          profile?.currentJobRole || roleHist[0]?.jobRole || user.jobRole,
          profile?.currentJobLevel ?? roleHist[0]?.jobLevel ?? user.jobLevel
        ),
        designation: profile?.designation || '',
        personalityTest: profile?.personalityTestResult || '',
        expertise: profile?.expertise || [],
        joiningDate: profile?.joiningDate || user.joinDate || null,
        prevEmployer,
        prevEmployerName: prevEmployer?.name || '—',
        experience: totalExperience,
        matrixSalary,
        salaryEntries,
        officeLocation,
        bonus: yearBonuses,
        bonusTotal: (yearBonuses.q1 || 0) + (yearBonuses.q2 || 0) + (yearBonuses.q3 || 0) + (yearBonuses.q4 || 0),
        noticePeriod,
        graduationDate: profile?.graduationDate || null,
        degreeLabel,
        degreeTooltip,
        hadInternship,
        internTooltip,
        planningMasters: profile?.planningMasters || '',
        profileIntro: profile?.profileIntro || '',
        last3Reviews,
        promotionReadiness,
        latestReviewId,
        last3Evals,
      };
    });
  }, [activeUsers, overview, ledgerMap, getEmployeeNoticeEntitlement, selectedLeaveYear, getSalaryFromMatrix, getSalaryFromMatrixHistory, getSalaryFromNLMatrixHistory]);

  // ── Unique values for filters ────────────────────────────────────────
  const uniqueValues = useMemo(() => {
    const vals = {};
    const collect = (key, extractor) => {
      const set = new Set();
      rows.forEach(r => { const v = extractor(r); if (v) set.add(v); });
      vals[key] = Array.from(set).sort();
    };
    collect('name', r => r.name);
    collect('office', r => r.office);
    collect('homeCity', r => r.homeCity);
    collect('roleLevel', r => r.roleLevelRaw);
    collect('designation', r => r.designation);
    collect('degree', r => r.degreeLabel !== '—' ? r.degreeLabel : null);
    collect('internship', r => r.hadInternship ? 'Yes' : 'No');
    collect('promotionReadiness', r => r.promotionReadiness !== '—' ? r.promotionReadiness : null);
    return vals;
  }, [rows]);

  // ── Apply filters ────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    let result = rows;

    // Active / all users filter (mirrors TeamMembersPage: isActive !== false)
    if (!showAllUsers) {
      result = result.filter(r => r.isActive !== false);
    }

    // Text search
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      result = result.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.homeCity.toLowerCase().includes(q) ||
        r.roleLevelRaw.toLowerCase().includes(q) ||
        r.designation.toLowerCase().includes(q)
      );
    }

    // Filter rules
    filterRules.forEach(rule => {
      if (!rule.value && rule.condition !== 'contains') return;
      result = result.filter(r => {
        let cellVal = '';
        switch (rule.key) {
          case 'name': cellVal = r.name; break;
          case 'office': cellVal = r.office; break;
          case 'homeCity': cellVal = r.homeCity; break;
          case 'roleLevel': cellVal = r.roleLevelRaw; break;
          case 'designation': cellVal = r.designation; break;
          case 'degree': cellVal = r.degreeLabel; break;
          case 'internship': cellVal = r.hadInternship ? 'Yes' : 'No'; break;
          case 'promotionReadiness': cellVal = r.promotionReadiness; break;
          default: cellVal = '';
        }
        if (rule.condition === 'is') return cellVal === rule.value;
        if (rule.condition === 'is not') return cellVal !== rule.value;
        if (rule.condition === 'contains') return cellVal.toLowerCase().includes((rule.value || '').toLowerCase());
        return true;
      });
    });

    return result;
  }, [rows, searchText, filterRules, showAllUsers]);

  // ── Apply sorting ────────────────────────────────────────────────────
  const sortedRows = useMemo(() => {
    if (sortRules.length === 0) return filteredRows;
    const sorted = [...filteredRows];
    sorted.sort((a, b) => {
      for (const rule of sortRules) {
        let va, vb;
        switch (rule.key) {
          case 'name': va = a.name; vb = b.name; break;
          case 'office': va = a.office; vb = b.office; break;
          case 'dob': va = a.dob || ''; vb = b.dob || ''; break;
          case 'homeCity': va = a.homeCity; vb = b.homeCity; break;
          case 'roleLevel': va = a.roleSortOrder; vb = b.roleSortOrder; break;
          case 'designation': va = a.designation; vb = b.designation; break;
          case 'personalityTest': va = a.personalityTest; vb = b.personalityTest; break;
          case 'joiningDate': va = a.joiningDate || ''; vb = b.joiningDate || ''; break;
          case 'prevEmployer': va = a.prevEmployerName; vb = b.prevEmployerName; break;
          case 'experience': va = Number(a.experience) || 0; vb = Number(b.experience) || 0; break;
          case 'salaryHistory': va = a.matrixSalary || 0; vb = b.matrixSalary || 0; break;
          case 'bonus': va = a.bonusTotal || 0; vb = b.bonusTotal || 0; break;
          case 'noticePeriod': va = a.noticePeriod || 0; vb = b.noticePeriod || 0; break;
          case 'graduationDate': va = a.graduationDate || ''; vb = b.graduationDate || ''; break;
          case 'degree': va = a.degreeLabel; vb = b.degreeLabel; break;
          case 'internship': va = a.hadInternship ? 1 : 0; vb = b.hadInternship ? 1 : 0; break;
          case 'planningMasters': va = a.planningMasters; vb = b.planningMasters; break;
          case 'promotionReadiness': va = a.promotionReadiness; vb = b.promotionReadiness; break;
          case 'perfRating': va = a.last3Evals[0]?.performanceRating || 0; vb = b.last3Evals[0]?.performanceRating || 0; break;
          default: va = ''; vb = '';
        }
        const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
        if (cmp !== 0) return rule.dir === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
    return sorted;
  }, [filteredRows, sortRules]);

  // ── Handle editable field save ───────────────────────────────────────
  const handleFieldSave = useCallback(async (userId, field, value) => {
    const result = await overview.updateProfileField(userId, field, value);
    if (!result.success) {
      console.error('Failed to save field:', result.error);
    }
  }, [overview]);

  const handlePromotionSave = useCallback(async (reviewId, value) => {
    const result = await overview.updatePromotionReadiness(reviewId, value);
    if (!result.success) {
      console.error('Failed to save promotion readiness:', result.error);
    }
  }, [overview]);

  const handleRatingSave = useCallback(async (evalId, rating) => {
    const parsed = rating === '' ? null : parseFloat(rating);
    const result = await overview.updateEvalRating(evalId, parsed);
    if (!result.success) {
      console.error('Failed to save rating:', result.error);
    }
  }, [overview]);

  // ── Bonus save (writes to users.bonuses[currentYear]) ──────────────
  const handleBonusSave = useCallback(async (userId, q1, q2, q3, q4) => {
    const user = activeUsers.find(u => u.id === userId);
    if (!user || !supabaseUsers) return;
    const currentYear = new Date().getFullYear();
    const updatedBonuses = {
      ...(user.bonuses || {}),
      [currentYear]: { q1, q2, q3, q4 },
    };
    const result = await supabaseUsers.updateUser(userId, { bonuses: updatedBonuses });
    if (!result.success) {
      console.error('Failed to save bonus:', result.error);
    }
  }, [activeUsers, supabaseUsers]);

  // ── Column header click for quick sort ───────────────────────────────
  const handleColumnSort = useCallback((key) => {
    const col = DEFAULT_COLUMNS.find(c => c.key === key);
    if (!col?.sortable) return;
    setSortRules(prev => {
      const existing = prev.find(r => r.key === key);
      if (existing) {
        if (existing.dir === 'asc') return prev.map(r => r.key === key ? { ...r, dir: 'desc' } : r);
        return prev.filter(r => r.key !== key);
      }
      return [{ key, dir: 'asc' }];
    });
  }, []);

  // ── Loading state ────────────────────────────────────────────────────
  if (overview.loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', fontFamily: FONT, color: BRIO_GREY }}>
        Loading team overview…
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════

  const ROW_STYLE = {
    fontSize: '11px', fontFamily: FONT, lineHeight: '16px',
    padding: '6px 6px', borderBottom: '1px solid ' + BRIO_BORDER,
    verticalAlign: 'top', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  };

  const HEADER_STYLE = {
    ...ROW_STYLE,
    fontWeight: 600, fontSize: '10px', color: BRIO_GREY,
    textTransform: 'uppercase', letterSpacing: '0.3px',
    background: BRIO_BG, position: 'sticky', top: 0, zIndex: 10,
    cursor: 'pointer', userSelect: 'none',
    whiteSpace: 'nowrap',
    padding: '9px 6px',
  };

  const RO_STYLE = {
    background: '#f8f8f6',
    color: '#555',
  };

  return (
    <div style={{ fontFamily: FONT, padding: '0' }}>
      {/* ── Page Header ─────────────────────────────────────────────── */}
      <div style={{
        padding: '16px 20px', borderBottom: '1px solid ' + BRIO_BORDER,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: '#fff',
      }}>
        <div>
          <span style={{ fontSize: '11px', color: BRIO_GREY }}>
            {sortedRows.length} of {activeUsers.length} members
          </span>
        </div>

        {/* ── Toolbar ──────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={12} style={{ position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search…"
              style={{
                fontSize: '11px', padding: '4px 6px 4px 22px', width: '140px',
                border: '1px solid ' + BRIO_BORDER, borderRadius: '4px', fontFamily: FONT,
                outline: 'none',
              }}
            />
          </div>

          {/* Active / All toggle */}
          <button
            onClick={() => setShowAllUsers(!showAllUsers)}
            style={{
              fontSize: '10px', fontWeight: 600, fontFamily: FONT,
              padding: '4px 8px', borderRadius: '4px', cursor: 'pointer',
              border: '1px solid ' + (showAllUsers ? BRIO_GOLD : BRIO_BORDER),
              background: showAllUsers ? BRIO_GOLD + '12' : 'transparent',
              color: showAllUsers ? BRIO_GOLD : BRIO_GREY,
              transition: 'all 0.15s ease',
            }}
          >
            {showAllUsers ? 'All' : 'Active'}
          </button>

          {/* Sort */}
          <div style={{ position: 'relative' }}>
            <ToolbarIcon
              onClick={() => { setShowSortPanel(!showSortPanel); setShowFilterPanel(false); }}
              tooltip="Sort"
              active={sortRules.length > 0}
              badge={sortRules.length || null}
            >
              <ArrowUpDown size={13} />
            </ToolbarIcon>
            {showSortPanel && (
              <SortPanel sortRules={sortRules} onChangeSortRules={setSortRules} onClose={() => setShowSortPanel(false)} />
            )}
          </div>

          {/* Filter */}
          <div style={{ position: 'relative' }}>
            <ToolbarIcon
              onClick={() => { setShowFilterPanel(!showFilterPanel); setShowSortPanel(false); }}
              tooltip="Filter"
              active={filterRules.length > 0}
              badge={filterRules.length || null}
            >
              <Filter size={13} />
            </ToolbarIcon>
            {showFilterPanel && (
              <FilterPanel
                filterRules={filterRules}
                onChangeFilterRules={setFilterRules}
                onClose={() => setShowFilterPanel(false)}
                uniqueValues={uniqueValues}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Active filter chips ─────────────────────────────────────── */}
      {filterRules.length > 0 && (
        <div style={{ padding: '6px 20px', display: 'flex', gap: '6px', flexWrap: 'wrap', background: '#fefef8', borderBottom: '1px solid ' + BRIO_BORDER }}>
          {filterRules.map((rule, idx) => {
            const col = FILTERABLE_COLUMNS.find(c => c.key === rule.key);
            return (
              <span key={idx} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                fontSize: '10px', padding: '2px 8px', background: BRIO_GOLD + '15',
                border: '1px solid ' + BRIO_GOLD + '30', borderRadius: '3px',
                fontFamily: FONT, color: BRIO_GOLD, fontWeight: 500,
              }}>
                {col?.label} {rule.condition} {rule.value}
                <button
                  onClick={() => setFilterRules(filterRules.filter((_, i) => i !== idx))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO_GOLD, padding: 0, lineHeight: 1 }}
                ><X size={10} /></button>
              </span>
            );
          })}
          <button
            onClick={() => setFilterRules([])}
            style={{ fontSize: '10px', color: BRIO_RED, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT }}
          >
            Clear all
          </button>
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────────────── */}
      <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 140px)', position: 'relative' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
          <colgroup>
            {orderedColumns.map(col => {
              const isCollapsed = collapsedCols[col.key];
              const w = isCollapsed ? COLLAPSED_W : col.flexW;
              return <col key={col.key} style={{ width: w + 'px', minWidth: (isCollapsed ? COLLAPSED_W : col.minW) + 'px' }} />;
            })}
          </colgroup>
          <thead>
            <tr>
              {orderedColumns.map((col, colIdx) => {
                const sortRule = sortRules.find(r => r.key === col.key);
                const isCollapsed = collapsedCols[col.key];
                const isFrozen = col.alwaysFrozen || frozenCols[col.key];
                const frozenLeft = isFrozen ? computeFrozenLeft(orderedColumns, collapsedCols, frozenCols, colIdx, COLLAPSED_W) : undefined;
                const isDragOver = dragOverColIdx === colIdx && dragColIdxRef.current !== colIdx;

                const stickyStyle = isFrozen ? {
                  position: 'sticky', left: frozenLeft + 'px', zIndex: 12,
                  borderRight: '2px solid ' + BRIO_GOLD + '40',
                } : {};

                if (isCollapsed) {
                  return (
                    <th key={col.key} style={{ ...HEADER_STYLE, ...stickyStyle, cursor: 'pointer', padding: '4px 2px', textAlign: 'center' }}
                      onClick={() => toggleColCollapse(col.key)} title={'Expand ' + col.label}
                    >
                      <ChevronRight size={10} style={{ color: BRIO_GREY }} />
                    </th>
                  );
                }
                return (
                  <th key={col.key}
                    data-col-idx={colIdx}
                    draggable={!col.alwaysFrozen && !frozenCols[col.key]}
                    onDragStart={e => {
                      if (col.alwaysFrozen || frozenCols[col.key]) { e.preventDefault(); return; }
                      dragColIdxRef.current = colIdx;
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', String(colIdx));
                    }}
                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverColIdx(colIdx); }}
                    onDragLeave={() => setDragOverColIdx(null)}
                    onDrop={e => {
                      e.preventDefault();
                      const fromIdx = dragColIdxRef.current;
                      if (fromIdx != null && fromIdx !== colIdx) moveColumn(fromIdx, colIdx);
                      dragColIdxRef.current = null; setDragOverColIdx(null);
                    }}
                    onDragEnd={() => { dragColIdxRef.current = null; setDragOverColIdx(null); }}
                    onTouchStart={e => handleTouchStart(colIdx, col, e)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    style={{
                      ...HEADER_STYLE, ...stickyStyle,
                      cursor: 'default', padding: '6px 5px',
                      borderLeft: isDragOver ? '2px solid ' + BRIO_GOLD : undefined,
                      WebkitUserSelect: 'none', userSelect: 'none',
                      touchAction: (!col.alwaysFrozen && !frozenCols[col.key]) ? 'none' : undefined,
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px', justifyContent: 'space-between' }}>
                      <span
                        onClick={() => handleColumnSort(col.key)}
                        style={{ cursor: col.sortable ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: '1px', flex: 1, overflow: 'hidden' }}
                      >
                        {col.label}
                        {sortRule && (
                          sortRule.dir === 'asc'
                            ? <ChevronUp size={9} style={{ color: BRIO_GOLD, flexShrink: 0 }} />
                            : <ChevronDown size={9} style={{ color: BRIO_GOLD, flexShrink: 0 }} />
                        )}
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
                        {!col.alwaysFrozen && (
                          <span
                            onClick={(e) => { e.stopPropagation(); toggleFreezeCol(col.key); }}
                            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: '1px', color: frozenCols[col.key] ? BRIO_GOLD : BRIO_GREY, opacity: frozenCols[col.key] ? 1 : 0.4 }}
                            title={frozenCols[col.key] ? 'Unfreeze column' : 'Freeze column'}
                          >
                            {frozenCols[col.key] ? <Lock size={10} /> : <Unlock size={10} />}
                          </span>
                        )}
                        {!col.alwaysFrozen && (
                          <span
                            onClick={(e) => { e.stopPropagation(); toggleColCollapse(col.key); }}
                            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: '1px', color: BRIO_GREY, flexShrink: 0 }}
                            title="Collapse column"
                          >
                            <ChevronLeft size={11} />
                          </span>
                        )}
                      </span>
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(row => {
              const cc = (key) => collapsedCols[key];
              const EMPTY_TD = { ...ROW_STYLE, padding: '4px 0' };
              return (
              <tr key={row.userId} style={{ transition: 'background 0.1s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fafaf4'; }}
                onMouseLeave={e => { e.currentTarget.style.background = ''; }}
              >
                {orderedColumns.map((col, colIdx) => {
                  const isFrozen = col.alwaysFrozen || frozenCols[col.key];
                  const frozenLeft = isFrozen ? computeFrozenLeft(orderedColumns, collapsedCols, frozenCols, colIdx, COLLAPSED_W) : undefined;
                  const frozenTdStyle = isFrozen ? {
                    position: 'sticky', left: frozenLeft + 'px', zIndex: 5,
                    background: '#fff', borderRight: '2px solid ' + BRIO_GOLD + '40',
                  } : {};

                  if (cc(col.key)) return <td key={col.key} style={{ ...EMPTY_TD, ...frozenTdStyle }} />;

                  return renderCell(col.key, row, colIdx, {
                    ROW_STYLE: { ...ROW_STYLE, ...frozenTdStyle },
                    RO_STYLE,
                    openFullscreen, handleFieldSave, setDrawer,
                    handlePromotionSave, handleRatingSave, handleBonusSave,
                  });
                })}
              </tr>
              );
            })}
          </tbody>
        </table>

        {sortedRows.length === 0 && !overview.loading && (
          <div style={{ padding: '40px', textAlign: 'center', color: BRIO_GREY, fontSize: '13px', fontFamily: FONT }}>
            No team members match your filters.
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          EXPANSION DRAWER (right-side detail panel)
          ══════════════════════════════════════════════════════════════════ */}
      {drawer && drawer.column === 'employers' && (
        <ExpansionDrawer
          title={`Previous Employers — ${drawer.data.name}`}
          onClose={() => setDrawer(null)}
        >
          {drawer.data.emps.map((emp, i) => (
            <div key={emp.id} style={{
              padding: '10px 12px', marginBottom: '8px',
              background: BRIO_BG, border: '1px solid ' + BRIO_BORDER, borderRadius: '4px',
            }}>
              <div style={{ fontWeight: 600, fontSize: '12px', color: '#333' }}>{emp.name}</div>
              <div style={{ fontSize: '11px', color: BRIO_GREY, marginTop: '2px' }}>{emp.role}</div>
              <div style={{ fontSize: '10px', color: BRIO_GREY, marginTop: '4px' }}>
                {fmtDate(emp.startDate)} — {emp.endDate ? fmtDate(emp.endDate) : 'Present'}
              </div>
            </div>
          ))}
        </ExpansionDrawer>
      )}


      {drawer && drawer.column === 'reviews' && (
        <ExpansionDrawer
          title={`Performance Reviews — ${drawer.data.name}`}
          onClose={() => setDrawer(null)}
        >
          {drawer.data.last3Reviews.map((review, i) => {
            const sF = review.sectionF || {};
            return (
              <div key={review.id} style={{
                padding: '12px', marginBottom: '8px',
                background: BRIO_BG, border: '1px solid ' + BRIO_BORDER, borderRadius: '4px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 600, fontSize: '12px', color: BRIO_GOLD }}>
                    {fmtDate(review.reviewDate)}
                  </span>
                  {review.overallRating != null && (
                    <span style={{
                      fontSize: '10px', padding: '2px 7px',
                      background: 'rgba(132,164,140,0.08)', color: BRIO_GREEN,
                      borderRadius: '10px', fontWeight: 500, border: '1px solid rgba(132,164,140,0.3)',
                    }}>
                      ★{review.overallRating}
                    </span>
                  )}
                </div>
                {sF.overallSummary && (
                  <div style={{ fontSize: '11px', color: '#333', lineHeight: '1.5', marginBottom: '6px' }}>
                    {sF.overallSummary}
                  </div>
                )}
                {sF.promotionReadiness && (
                  <div style={{ fontSize: '10px', color: BRIO_GREY }}>
                    Promotion readiness: <span style={{
                      fontWeight: 500,
                      color: sF.promotionReadiness === 'Ready' ? BRIO_GREEN
                        : sF.promotionReadiness === 'Possible' ? BRIO_ORANGE
                        : BRIO_GREY,
                    }}>{sF.promotionReadiness}</span>
                  </div>
                )}
                {sF.keyStrengths && sF.keyStrengths.filter(Boolean).length > 0 && (
                  <div style={{ fontSize: '10px', color: BRIO_GREY, marginTop: '4px' }}>
                    Strengths: {sF.keyStrengths.filter(Boolean).join(', ')}
                  </div>
                )}
                <div style={{ marginTop: '6px' }}>
                  <span
                    onClick={() => openFullscreen(drawer.data.userId)}
                    style={{ fontSize: '10px', color: BRIO_GOLD, cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}
                  >
                    Open full review →
                  </span>
                </div>
              </div>
            );
          })}
        </ExpansionDrawer>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          FULLSCREEN TEAM MEMBER OVERLAY
          ══════════════════════════════════════════════════════════════════ */}
      {fullscreenUserId && (
        <TeamMemberFullScreenPage
          userId={fullscreenUserId}
          onClose={closeFullscreen}
          currentUser={currentUser}
          memberUser={activeUsers.find(u => String(u.id) === String(fullscreenUserId))}
          supabaseUsers={supabaseUsers}
          activeTeams={activeTeams}
          activeTimeEntries={activeTimeEntries}
          activeProjects={activeProjects}
          getInternalCostRate={getInternalCostRate}
        />
      )}
    </div>
  );
}
